<script setup lang="ts">
import { INQUIRY_STATUS, INQUIRY_ITEM } from '@/types'
import {
  getInitialInquiryStatus,
  sendChangedInquiryStatusEvent,
} from '@/utils/mixpanel'

const emit = defineEmits<{
  (e: 'open', value: boolean): void
  (e: 'close', value: boolean): void
}>()

const props = defineProps({
  selectedInquiry: {
    type: Object as PropType<INQUIRY_ITEM>,
    required: false,
  },
  desktopDetails: {
    type: Boolean,
    required: false,
  },
})

const inquiriesStore = props.desktopDetails
  ? useDesktopDetailsStore()
  : useInquiriesStore()
const appStore = useAppStore()
const router = useRouter()
const route = useRoute()

const showConfirmStatusAndCapturePaymentPopUp = ref(false)
const isDisabled = ref(false)
const showRejectOrderConfirmationPopUp = ref(false)
const showRefundOrderConfirmationPopUp = ref(false)
const rejectionComment = ref('')

watch(() => inquiriesStore.openAcceptOrderModal, (val) => {
  if (val) {
    inquiriesStore.setSelectedInquiryStatus('accepted')
    showConfirmStatusAndCapturePaymentPopUp.value = true
    inquiriesStore.setOpenAcceptOrderModal(false)
  }
}, { immediate: true })

const anyOpen = computed(
  () =>
    showConfirmStatusAndCapturePaymentPopUp.value ||
    showRejectOrderConfirmationPopUp.value ||
    showRefundOrderConfirmationPopUp.value
)

const inquiry = computed(() => {
  if (props.desktopDetails) {
    return inquiriesStore.inquiry
  } else {
    return (
      inquiriesStore.getInquiryById(Number(route.query.inquiry_id)) ||
      JSON.parse(JSON.stringify(props.selectedInquiry))
    )
  }
})

const isOrder = computed(() => {
  return inquiry.value!.is_order
})

const currentSpotName = appStore.spotInfo.name

const changeStatusAndCapturePayment = async () => {
  isDisabled.value = true
  showConfirmStatusAndCapturePaymentPopUp.value = false
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!
  if (inquiry.value!.pick_up_order_status) {
    inquiry.value!.pick_up_order_status = inquiriesStore.selectedInquiryStatus!
  } else if (inquiry.value!.delivery_order_status) {
    inquiry.value!.delivery_order_status = inquiriesStore.selectedInquiryStatus!
  }

  sendChangedInquiryStatusEvent(inquiry.value, oldStatus, route.name as string)
  inquiriesStore.updateInquiry(inquiry.value!)
  await nextTick()
  isDisabled.value = false
}

const handleCloseConfirmStatusAndCapturePaymentPopUp = () => {
  showConfirmStatusAndCapturePaymentPopUp.value = false
  resetStatusBtn()
  deleteInquiryStatusParameter()
}

const handleRejectOrder = async () => {
  isDisabled.value = true
  showRejectOrderConfirmationPopUp.value = false
  inquiriesStore.setSelectedInquiryStatus('rejected')
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!

  if (inquiry.value!.pick_up_order_status) {
    inquiry.value!.pick_up_order_status = inquiriesStore.selectedInquiryStatus!
  } else if (inquiry.value!.delivery_order_status) {
    inquiry.value!.delivery_order_status = inquiriesStore.selectedInquiryStatus!
  }

  sendChangedInquiryStatusEvent(inquiry.value, oldStatus, route.name as string)
  inquiriesStore.updateInquiry(inquiry.value!)
  await handleRejectionComment()
  isDisabled.value = false
}

const handleRejectionComment= async() => {
  const reason = rejectionComment.value.trim()
  if (reason.length > 0) {
    const comment = await inquiriesStore.createInquiryComment(
      inquiry!.value!.id,
      reason,
      'manual',
    )
    inquiriesStore.comments.push(comment)
    rejectionComment.value = ''
  }  
}

const handleCloseRejectOrderConfirmationPopUp = () => {
  showRejectOrderConfirmationPopUp.value = false
  rejectionComment.value = ''
  resetStatusBtn()
}

function resetStatusBtn() {
  if (inquiry.value!.pick_up_order_status) {
    inquiriesStore.setSelectedInquiryStatus(inquiry.value!.pick_up_order_status)
  } else if (inquiry.value!.delivery_order_status) {
    inquiriesStore.setSelectedInquiryStatus(
      inquiry.value!.delivery_order_status,
    )
  }
}

const handleRefundOrder = async () => {
  isDisabled.value = true
  showRefundOrderConfirmationPopUp.value = false
  inquiriesStore.setSelectedInquiryStatus('rejected')
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!

  if (inquiry.value!.pick_up_order_status) {
    inquiry.value!.pick_up_order_status = inquiriesStore.selectedInquiryStatus!
  } else if (inquiry.value!.delivery_order_status) {
    inquiry.value!.delivery_order_status = inquiriesStore.selectedInquiryStatus!
  }

  sendChangedInquiryStatusEvent(inquiry.value, oldStatus, route.name as string)
  inquiriesStore.updateInquiry(inquiry.value!)
  await handleRejectionComment()
  isDisabled.value = false
}

const handleCloseRefundOrderConfirmationPopUp = () => {
  showRefundOrderConfirmationPopUp.value = false
  rejectionComment.value = ''
  resetStatusBtn()
}

const deleteInquiryStatusParameter = () => {
  delete route.query.inquiry_status
  const { href } = router.resolve(route)
  window.history.pushState({}, '', href)
}

const updateInquiryStatus = (newValue: INQUIRY_STATUS) => {
  const oldStatus = getInitialInquiryStatus(inquiry.value!)!

  if (inquiry.value!.pick_up_order_status) {
    inquiry.value!.pick_up_order_status = newValue
  } else if (inquiry.value!.delivery_order_status) {
    inquiry.value!.delivery_order_status = newValue
  }

  sendChangedInquiryStatusEvent(inquiry.value!, oldStatus, route.name as string)

  inquiriesStore.updateInquiry(inquiry.value!)
}

const setRejectionComment = () => {
  rejectionComment.value = `Dear ${inquiry!.value!.name},\nWe are sorry, but we cannot accept your order. \n\nBest Regards,\n${currentSpotName}`
}

if (
  route.query.inquiry_status === 'accepted' &&
  inquiry.value!.payment_status &&
  isOrder.value
) {
  showConfirmStatusAndCapturePaymentPopUp.value = true
  inquiriesStore.setSelectedInquiryStatus('accepted')
  deleteInquiryStatusParameter()
} else if (route.query.inquiry_status) {
  inquiriesStore.setSelectedInquiryStatus(
    route.query.inquiry_status as INQUIRY_STATUS,
  )

  if (inquiry.value!.pick_up_order_status) {
    inquiry.value!.pick_up_order_status = inquiriesStore.selectedInquiryStatus!
  } else if (inquiry.value!.delivery_order_status) {
    inquiry.value!.delivery_order_status = inquiriesStore.selectedInquiryStatus!
  }

  inquiriesStore.updateInquiry(inquiry.value!)

  deleteInquiryStatusParameter()
}

watch(
  () => inquiriesStore.selectedInquiryStatus,
  (newValue) => {
    if (isDisabled.value) return 
    if (newValue === inquiry.value!.delivery_order_status || newValue === inquiry.value!.pick_up_order_status) return
    if (newValue) {
      if (!inquiry.value!.payment_status) {
        if (newValue == 'rejected' && isOrder.value){
          showRejectOrderConfirmationPopUp.value = true
          setRejectionComment()
        } else {
          const message = localStorage.getItem(
            `message_${route.query.inquiry_id}`,
          )
          //mixpanel: prevent changing status automatically if user has written a message and then changes status
          if (message) {
            return
          }

          updateInquiryStatus(newValue)
        }
        return
      }

      if (
        inquiry.value!.pick_up_order_status == 'received' &&
        (newValue == 'accepted' ||
          newValue == 'ready for pick up' ||
          newValue == 'picked up') &&
        inquiry.value!.payment_status &&
        isOrder.value
      ) {
        showConfirmStatusAndCapturePaymentPopUp.value = true
        return
      } else if (
        inquiry.value!.delivery_order_status == 'received' &&
        (newValue == 'accepted' ||
          newValue == 'in route' ||
          newValue == 'delivered') &&
        inquiry.value!.payment_status &&
        isOrder.value
      ) {
        showConfirmStatusAndCapturePaymentPopUp.value = true
        return
      } else if (newValue == 'rejected') {
        if (
          inquiry.value!.delivery_order_status == 'received' ||
          inquiry.value!.pick_up_order_status == 'received'
        ) {
          setRejectionComment()
          showRejectOrderConfirmationPopUp.value = true
          return
        } else {
          setRejectionComment()
          showRefundOrderConfirmationPopUp.value = true
          return
        }
      }

      const message = localStorage.getItem(`message_${route.query.inquiry_id}`)
      //mixpanel: prevent changing status automatically if user has written a message and then changes status
      if (message) {
        return
      }
      updateInquiryStatus(newValue)
    }
  },
)

watch(anyOpen, (v) => {
  if (v) emit('open', true)
  else emit('close', false)
  }, 
  { immediate: true }
)

</script>

<template>
  <AppDialog
    :is-open="showConfirmStatusAndCapturePaymentPopUp"
    :can-close="true"
    :has-close-button="false"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="handleCloseConfirmStatusAndCapturePaymentPopUp"
  >
    <template #content>
      <div class="px-4 pb-4 text-center text-brand-black">
        <p>
          By changing the status to
          {{ inquiriesStore.selectedInquiryStatus ?? 'accepted' }} you are accepting this
          order and this customer will immediately be charged.
        </p>
        <p class="mt-2 font-semibold text-brand-black">
          Do you want to proceed?
        </p>
        <div class="flex gap-4">
          <AppButton
            class="mt-4 !ring-0"
            variant="neutral"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            @click="handleCloseConfirmStatusAndCapturePaymentPopUp"
          >
            No
          </AppButton>
          <AppButton
            tabindex="-1"
            class="mt-4"
            size="md"
            variant="success_yes"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isDisabled"
            @click="changeStatusAndCapturePayment"
          >
            Yes
          </AppButton>
        </div>
      </div>
    </template>
  </AppDialog>
  <AppDialog
    :is-open="showRejectOrderConfirmationPopUp"
    :can-close="true"
    :has-close-button="false"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="handleCloseRejectOrderConfirmationPopUp"
  >
    <template #content>
      <div class="px-4 pb-4 text-brand-black">
        <p v-if="inquiry?.payment_status" class="font-bold text-brand-black">
          Please confirm that you want to reject this order. If this order is
          rejected, you will not be able to change your mind, accept the order
          and charge this customer for this order.
        </p>
        <p v-else class="text-brand-black">
          Please provide a reason for rejection that will be emailed to the customer:
        </p>
        <p v-if="inquiry?.payment_status" class="mt-2 text-brand-black">
          Please provide a reason for rejection that will be emailed to the customer:
        </p>
        <textarea
          v-model="rejectionComment"
          rows="10"
          placeholder="Reason for rejection..."
          class="mt-5 max-h-[200px] w-full rounded-lg border border-[#EEF1F7] bg-[#EEF1F7] p-3 text-brand-black placeholder:italic"
        ></textarea>
        <div class="flex gap-4">
          <AppButton
            tabindex="-1"
            class="mt-4 !bg-[#EEF1F7]"
            size="md"
            variant="secondary"
            :is-rounded="true"
            :is-wide="true"
            @click="handleCloseRejectOrderConfirmationPopUp"
          >
            No
          </AppButton>
          <AppButton
            class="mt-4 !bg-[#6DDF9D] !text-brand-black !ring-0"
            variant="primary"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isDisabled"
            @click="handleRejectOrder"
          >
            Yes
          </AppButton>
        </div>
      </div>
    </template>
  </AppDialog>
  <AppDialog
    :is-open="showRefundOrderConfirmationPopUp"
    :can-close="true"
    :has-close-button="false"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="handleCloseRefundOrderConfirmationPopUp"
  >
    <template #content>
      <div class="px-4 pb-4 text-brand-black">
        <p class="font-bold text-brand-black">
          Since this order was previously accepted, which triggered a payment,
          rejecting this order now will result in issuing a refund. Are you sure
          you want to proceed and reject this order now?
        </p>
        <p class="mt-2">
          Please provide a reason for rejection that will be emailed to the customer:
        </p>
        <textarea
          v-model="rejectionComment"
          rows="10"
          placeholder="Reason for rejection..."
          class="mt-5 max-h-[200px] w-full rounded-lg border border-[#EEF1F7] bg-[#EEF1F7] p-3 text-brand-black placeholder:italic"
        ></textarea>
        <div class="flex gap-4">
          <AppButton
            tabindex="-1"
            class="mt-4 !bg-[#EEF1F7]"
            size="md"
            variant="secondary"
            :is-rounded="true"
            :is-wide="true"
            @click="handleCloseRefundOrderConfirmationPopUp"
          >
            No
          </AppButton>
          <AppButton
            class="mt-4 !bg-[#6DDF9D] !text-brand-black !ring-0"
            variant="primary"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isDisabled"
            @click="handleRefundOrder"
          >
            Yes
          </AppButton>
        </div>
      </div>
    </template>
  </AppDialog>
</template>
